// 3rd party
import React, { ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import { useWizard } from 'react-use-wizard';
import { useTranslation } from 'react-i18next';

// Types
import { UserScopes, WizardFooterProps } from 'types';
import { CartState, SFCartItem } from 'types/parts/cart';
import { PermissionScopeName } from 'types/user-management';

// Constants
import { sfEnabled } from 'constants/featureFlags';
import breakpoint from 'constants/breakpoints';

// Components
import { Button, Loader, PermissionWrapper, Typography } from 'components';
import { useCart } from 'selectors';
import { useDispatch } from 'react-redux';
import { cartActions } from 'actions';
import { generateAnalyticsEvent } from 'common/helpers/v1/analytics';
import { AnalyticsCategories, ECommerceAnalyticEventActions } from 'constants/analytics';
import { useDeleteCartProductsMutation, useClearRemoteCartMutation, useGetRemoteCartItemsQuery } from 'api';
import theme from 'themes';
import { currency } from 'common/helpers/v1';
import { CartPriceType, getSelectedCartState } from './helper';
import { OrderInputWithPriceInfo } from 'types/parts';
import ConfirmationModal from 'common/components/ConfirmationModal';
import TriangleWarning from 'icons/indexed-icons/TriangleWarning';

// Styling
const Footer = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  width: 100%;
  padding: 1.5rem;
  border-bottom-left-radius: 0.625rem;
  border-bottom-right-radius: 0.625rem;
  border-top: 2px solid ${theme.colors.lightGrey3};

  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  position: absolute;
  bottom: 0;
  .icon-width {
    width: 30px;
  }
`;

const DLContainer = styled.div`
  flex: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: ${breakpoint.size.md}px) {
    width: 100%;
    margin-bottom: 1rem;
    align-items: right;
  }
`;
const TotalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 0.5rem;
  margin-right: 1rem;
`;

const GrandTotalTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`;

const GrandTotalPriceContainer = styled.div`
  display: flex;
  line-height: 1.2rem;
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;

  @media (max-width: ${breakpoint.size.md}px) {
    width: 100%;
    justify-content: right;
  }
`;

const Spacer = styled.div`
  // flex: 0 0 1rem;
`;

const NextContainer = styled.div`
  display: flex;
  align-items: center;
`;

interface Props {
  stepFooters: WizardFooterProps[];
  handleClose?: () => void;
  isLoading?: boolean;
  selectedPriceType?: CartPriceType;
  orderPriceInfo?: {
    pricedInfo?: OrderInputWithPriceInfo;
    nonePricedInfo?: OrderInputWithPriceInfo;
  };
  priceCalculationLoading?: boolean;
  isTwoWaySyncEnabled?: boolean;
}

const CartFooter = ({
  stepFooters,
  handleClose,
  selectedPriceType,
  isLoading,
  orderPriceInfo,
  priceCalculationLoading,
  isTwoWaySyncEnabled
}: Props): ReactElement => {
  const cartState = useCart() as CartState;
  const dispatch = useDispatch();
  const { t } = useTranslation(['fpns']);
  const [showClearCartConfirmation, setShowClearCartConfirmation] = React.useState(false);
  const { activeStep, isFirstStep, isLastStep, nextStep, previousStep, goToStep } = useWizard();
  const currentFooter = stepFooters[activeStep];
  const [deleteCartProducts] = useDeleteCartProductsMutation();
  const [orderPlacementProcessing, setOrderPlacementProcessing] = React.useState(false);

   const [clearRemoteCart] = useClearRemoteCartMutation();
  const {refetch: refetchRemoteCart} = useGetRemoteCartItemsQuery();
  const handleNext = () => {
    if (isLastStep) setOrderPlacementProcessing(true);
    if (currentFooter?.nextAction) currentFooter.nextAction();
    nextStep();
  };
  const cart = getSelectedCartState(cartState, selectedPriceType, activeStep > 1);
  const noSelectionChoice =
    activeStep > 1 && currentFooter.nextText === 'continue' && selectedPriceType === 'nill';

  const emptyCart = () => {
    if (sfEnabled()) {
      cart.sfCart.cartItems?.map((sfItem: SFCartItem) => {
        deleteCartProducts({ cartItemId: sfItem.cartItem.cartItemId })
          .unwrap()
          .then(() => {
            dispatch({ type: cartActions.EMPTY_CART });
            goToStep(1);
          });
      });
    } else {
        clearRemoteCart().unwrap();
        dispatch({ type: cartActions.EMPTY_CART });
    }
    setShowClearCartConfirmation(false);
  };

  // Adjust to show by multiple currencies.
  // Customers should not see multiple currencies but, putting this
  // logic in so things do not appear broken for cross-region users (i.e. admins)
  const currencyGroups: Record<string, number> = {};
  cart?.items.forEach((itemGroup) => {
    // For now, assume undefined priceUnit/currency defaults to USD
    // for items that do not have a currency from the
    const priceUnit = itemGroup.item.priceUnit || 'USD';
    const price = !itemGroup.item?.isPurchasable
      ? 0
      : (itemGroup.item?.custPrice ? itemGroup.item.custPrice : itemGroup.item.price || 0) *
        itemGroup.quantity;
    if (price) {
      if (Object.prototype.hasOwnProperty.call(currencyGroups, priceUnit))
        currencyGroups[priceUnit] += price;
      else currencyGroups[priceUnit] = price;
    }
  });

  const grandTotal = Object.keys(currencyGroups)
    .map((priceUnit) => {
      return currency(currencyGroups[priceUnit], priceUnit);
    })
    .join(', ');
  // use a useMemo hook to evalute final total, user:
  // (subtotalExTax + taxAmount - totalDiscount).toFixed(2)
  const finalTotal = useMemo(() => {
    if (orderPriceInfo) {
      const { pricedInfo } = orderPriceInfo;
      const { subtotal, tax, discount, currencyCode } = pricedInfo || {};
      return currency(subtotal! + tax! - discount!, currencyCode || 'USD');
    }
    return '';
  }, [orderPriceInfo]);
  const nonePricedFinalStage = !isTwoWaySyncEnabled && isLastStep;
  return (
    <Footer>
      <DLContainer>
        {!isFirstStep && !nonePricedFinalStage && (
          <Button
            variant="outline-blue"
            borderColor={theme.colors.primaryBlue5}
            onClick={previousStep}
            disabled={isLoading || orderPlacementProcessing || priceCalculationLoading}
            height="2.5rem"
          >
            {t('back')}
          </Button>
        )}
      </DLContainer>

      <Spacer />

      {activeStep < 3 && !isLastStep && (
        <TotalContainer>
          <GrandTotalTextContainer>
            <Typography color={theme.colors.lightGrey8} mb={'.2rem'} size="1rem">
              {t('machine_subtotal')}
            </Typography>
            <GrandTotalPriceContainer>
              <Typography weight="semi-bold" mb={0} size="1.2rem">
                {grandTotal !== '' ? String(grandTotal) : 'N/A'}
              </Typography>
            </GrandTotalPriceContainer>
          </GrandTotalTextContainer>
        </TotalContainer>
      )}
      <ButtonContainer>
        {activeStep < 2 && (
          <Button
            disabled={cart?.items.length === 0}
            variant="outline-blue"
            width="8rem"
            onClick={() => setShowClearCartConfirmation(true)}
            height="2.5rem"
          >
            {t('clear_cart')}
          </Button>
        )}

        {showClearCartConfirmation && (
          <ConfirmationModal
            isConfirmPrompt
            isVisible={showClearCartConfirmation}
            title={'Delete Cart?'}
            headerIcon={<TriangleWarning />}
            zIndex={99999}
            onCancelCallback={() => setShowClearCartConfirmation(false)}
            onConfirmCallback={emptyCart}
          >
            <Typography mb={0}>
              Deleting the cart will delete all the items in cart. Do you want to continue?
            </Typography>
          </ConfirmationModal>
        )}
        {isLastStep && (
          <>
            {priceCalculationLoading ? (
              <Loader size={35} />
            ) : !nonePricedFinalStage ? (
              <TotalContainer>
                <GrandTotalTextContainer>
                  <Typography color={theme.colors.lightGrey8} mb={'.2rem'} size="1rem">
                    {t('machine_final_total')}
                  </Typography>
                  <GrandTotalPriceContainer>
                    <Typography weight="semi-bold" mb={0} size="1.2rem">
                      {finalTotal !== '' ? finalTotal : 'N/A'}
                    </Typography>
                  </GrandTotalPriceContainer>
                </GrandTotalTextContainer>
              </TotalContainer>
            ) : (
              ''
            )}
            <Button
              variant="outline-blue"
              width="8rem"
              height="2.5rem"
              onClick={handleClose}
              disabled={orderPlacementProcessing || priceCalculationLoading}
            >
              {t('close')}
            </Button>
          </>
        )}
        <NextContainer>
          <PermissionWrapper scope={UserScopes.Write} page={PermissionScopeName.SHOP}>
            <Button
              disabled={
                currentFooter?.continueCondition
                  ? !currentFooter.continueCondition?.() ||
                    orderPlacementProcessing ||
                    priceCalculationLoading ||
                    noSelectionChoice
                  : false
              }
              variant="filled-primary"
              height="2.5rem"
              // width="8rem"
              onClick={() => {
                if (!isFirstStep || !isLastStep) {
                  generateAnalyticsEvent({
                    category: AnalyticsCategories.ECOMMERCE,
                    action: ECommerceAnalyticEventActions.ADD_SHIPPING_INFO
                  });
                }
                handleNext();
              }}
            >
              {t(currentFooter?.nextText, { ns: ['fpns', 'common'] })}
            </Button>
          </PermissionWrapper>
        </NextContainer>
      </ButtonContainer>
    </Footer>
  );
};

export default CartFooter;
